import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { CreditCard, Save, Loader2, X, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@utils';
import api from '@services/api';

interface ConfigField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

interface GatewayData {
  id: number;
  name: string;
  displayName: string;
  code: string;
  description: string;
  logo: string | null;
  isEnabled: boolean;
  isConfigured: boolean;
  configSchema: {
    fields: ConfigField[];
  } | null;
  settings: Record<string, any> | null;
}

export default function PaymentSettingsPage() {
  const [selectedGateway, setSelectedGateway] = useState<GatewayData | null>(null);
  const queryClient = useQueryClient();

  const { data: gateways, isLoading, error } = useQuery<{ data: GatewayData[] }>({
    queryKey: ['payment-gateways'],
    queryFn: () => api.get<{ data: GatewayData[] }>('/settings/payment'),
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 dark:bg-gray-700" />
        <div className="grid grid-cols-1 gap-6">
          <div className="h-40 bg-gray-200 rounded-xl dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  const gatewayList = gateways?.data || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configure payment gateways to accept payments for your events.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
          Failed to load payment gateways. Please refresh the page.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {gatewayList.map((gateway) => (
          <div key={gateway.id} className="card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center shrink-0",
                gateway.isEnabled ? "bg-primary-50 dark:bg-primary-900/20" : "bg-gray-100 dark:bg-gray-800"
              )}>
                {gateway.logo ? (
                  <img src={gateway.logo} alt={gateway.name} className="w-8 h-8 object-contain" />
                ) : (
                  <CreditCard className={cn(
                    "w-6 h-6",
                    gateway.isEnabled ? "text-primary-600" : "text-gray-400"
                  )} />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  {gateway.displayName || gateway.name}
                  {gateway.isEnabled && (
                    <span className="badge bg-success-100 text-success-800 border-success-200 text-xs">Active</span>
                  )}
                  {gateway.isConfigured && !gateway.isEnabled && (
                    <span className="badge bg-gray-100 text-gray-600 border-gray-200 text-xs">Configured</span>
                  )}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{gateway.description}</p>
              </div>
            </div>
            <button onClick={() => setSelectedGateway(gateway)} className="btn-secondary w-full sm:w-auto">
              Configure
            </button>
          </div>
        ))}

        {gatewayList.length === 0 && !error && (
          <div className="card p-12 text-center text-gray-500">
            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Payment Gateways</h3>
            <p>Payment gateway configuration will be available once gateways are set up in the backend.</p>
          </div>
        )}
      </div>

      {selectedGateway && (
        <GatewayConfigModal
          gateway={selectedGateway}
          onClose={() => setSelectedGateway(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['payment-gateways'] });
            setSelectedGateway(null);
          }}
        />
      )}
    </div>
  );
}

function GatewayConfigModal({ gateway, onClose, onSuccess }: { gateway: GatewayData; onClose: () => void; onSuccess: () => void }) {
  // Handle both array format (direct fields) and object format ({ fields: [...] })
  const schema = gateway.configSchema as any;
  const fields: ConfigField[] = Array.isArray(schema)
    ? schema
    : (schema?.fields || []);

  // Debug log to help troubleshoot
  console.log('Gateway Config Schema:', gateway.configSchema);
  console.log('Parsed Fields:', fields);

  const existingSettings = (gateway.settings && typeof gateway.settings === 'object' && !Array.isArray(gateway.settings))
    ? gateway.settings
    : {};

  const { register, handleSubmit, formState: { isSubmitting } } = useForm<any>({
    defaultValues: {
      is_enabled: gateway.isEnabled,
      display_name: gateway.displayName || gateway.name,
      ...existingSettings,
    }
  });

  const mutation = useMutation({
    mutationFn: (data: any) => {
      const { is_enabled, display_name, ...rest } = data;
      return api.put(`/settings/payment/${gateway.id}`, {
        is_enabled,
        display_name,
        config: rest,
      });
    },
    onSuccess: () => {
      toast.success(`${gateway.name} settings updated`);
      onSuccess();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save settings';
      toast.error(msg);
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-500" />
            Configure {gateway.name}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(data => mutation.mutate(data))} className="p-6 space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <input
              type="checkbox"
              {...register('is_enabled')}
              id="gw_enabled"
              className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="gw_enabled" className="font-medium text-gray-900 dark:text-white cursor-pointer">
              Enable {gateway.name}
            </label>
          </div>

          {/* Display name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Display Name</label>
            <input
              {...register('display_name')}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              placeholder="e.g. Credit Card (Tap)"
            />
            <p className="text-xs text-gray-500 mt-1">Name shown to customers at checkout.</p>
          </div>

          {/* Config fields */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 dark:text-white border-b pb-2 dark:border-gray-700">
              API Configuration
            </h4>

            {fields.length > 0 ? (
              fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'select' && field.name === 'mode' ? (
                    <select
                      {...register(field.name, { required: field.required })}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                    >
                      <option value="">Select mode</option>
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="live">Live (Production)</option>
                    </select>
                  ) : (
                    <input
                      type={field.type === 'password' ? 'password' : 'text'}
                      {...register(field.name, { required: field.required })}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">No additional configuration required.</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
